import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Shield, Users, CheckCircle, Scale, Award, Lightbulb } from 'lucide-react';
import InquiryModal from '../components/InquiryModal';

const About = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonContext, setButtonContext] = useState('');

  const handleButtonClick = (context) => {
    setButtonContext(context);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="relative bg-[#1A3628] py-24 overflow-hidden" data-testid="about-hero">
               <div className="absolute inset-0  bg-center" style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1695388474402-ed805a890d8d?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85")' }}><div className="absolute inset-0 bg-gradient-to-br from-[#1A3628]/90 via-[#11241A]/90 to-[#0D1910]/95"></div></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <p className="eyebrow text-[#C85A40] mb-6">About Our Firm</p>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white mb-8 tracking-tighter leading-tight">
            Protecting Brands Since <span className="text-[#C85A40]">2008</span>
          </h1>
          <p className="font-body text-base text-white/80 max-w-3xl mx-auto leading-relaxed">
            We are more than a registration service — we are your intellectual property counsel. With over 15 years of practice, 10,000 successful filings, and a 98% USPTO approval rate, we deliver measurable results that secure your competitive advantage.
          </p>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-24 bg-white" data-testid="about-differentiators">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="eyebrow text-[#C85A40] mb-4">Our Distinction</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[#111C16] tracking-tighter mb-4">Trademark Registration Experts</h2>
            <p className="font-body text-[#4A5D53] max-w-2xl mx-auto text-sm">
              Your brand deserves counsel that combines legal precision with commercial understanding. We protect not just your name — we protect your market position.
            </p>
          </div>

          <div className="max-w-5xl mx-auto mb-16">
            <div className="grid md:grid-cols-2 gap-0 border border-[#D5D1C8]">
              <div className="p-10 border-b md:border-b-0 md:border-r border-[#D5D1C8]">
                <h3 className="font-display text-2xl font-bold text-[#111C16] mb-6">Real Business Impact</h3>
                <p className="font-body text-sm text-[#4A5D53] leading-relaxed mb-4">
                  Our clients gain more than legal protection — they gain competitive advantages, investor confidence, and the freedom to scale without brand threats.
                </p>
                <p className="font-body text-sm text-[#4A5D53] leading-relaxed">
                  From startups to established enterprises, our streamlined process ensures your intellectual property becomes a strategic business asset, not just paperwork.
                </p>
              </div>
              <div className="p-10">
                <h3 className="font-display text-2xl font-bold text-[#111C16] mb-6">What Sets Us Apart</h3>
                <div className="space-y-5">
                  {[
                    { title: '98% Success Rate', desc: 'Industry-leading approval rate across all filings' },
                    { title: '24-48 Hour Filing', desc: 'Expedited protection without unnecessary delay' },
                    { title: 'Licensed Attorneys', desc: 'Every application reviewed by experienced IP counsel' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-[#C85A40] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div>
                        <p className="font-body text-sm font-semibold text-[#111C16]">{item.title}</p>
                        <p className="font-body text-sm text-[#4A5D53]">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
            {[
              { icon: Shield, title: 'Risk-Free Guarantee', desc: 'Money-back guarantee if we cannot secure your trademark' },
              { icon: Award, title: 'Strategic Advantage', desc: 'Position your brand for market dominance and investor appeal' },
              { icon: Users, title: 'Dedicated Counsel', desc: 'Direct access to your attorney throughout the process' },
              { icon: Scale, title: 'Proven Record', desc: '10,000+ enterprises protected with 98% approval success' }
            ].map((item, index) => (
              <div key={index} className="group p-8 bg-white border border-[#D5D1C8] hover-lift hover-gold-border" data-testid={`about-card-${index}`}>
                <div className="w-12 h-12 bg-[#1A3628] flex items-center justify-center mb-6 group-hover:bg-[#C85A40] transition-colors duration-300">
                  <item.icon className="h-6 w-6 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg font-bold text-[#111C16] mb-2">{item.title}</h3>
                <p className="font-body text-sm text-[#4A5D53] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Benefits */}
      <section className="py-24 bg-[#EAE7DF]" data-testid="about-benefits">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="eyebrow text-[#C85A40] mb-4">Business Benefits</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[#111C16] tracking-tighter">How We Protect Your Bottom Line</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {[
              { icon: Shield, title: 'Market Protection', desc: 'Prevent competitors from copying your brand. Exclusive rights mean exclusive market position — the legal authority to defend your territory.', metric: 'Avoid $50K+ in disputes' },
              { icon: Award, title: 'Investor Confidence', desc: 'Protected IP dramatically increases business valuation. Investors pay premium multiples for businesses with secured trademarks.', metric: 'Up to 3x higher valuation' },
              { icon: Lightbulb, title: 'Revenue Expansion', desc: 'Trademarks unlock licensing opportunities, Amazon Brand Registry, and premium positioning. Protected brands command higher prices.', metric: 'New revenue streams' },
              { icon: Scale, title: 'Risk Mitigation', desc: 'Avoid costly rebranding, litigation, and marketplace bans. Filing early prevents conflicts that could cost hundreds of thousands.', metric: 'Eliminate expensive risks' }
            ].map((value, index) => (
              <div key={index} className="group p-10 bg-white border border-[#D5D1C8] hover-lift hover-gold-border" data-testid={`about-benefit-${index}`}>
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 bg-[#1A3628] flex items-center justify-center group-hover:bg-[#C85A40] transition-colors duration-300">
                    <value.icon className="h-6 w-6 text-white" strokeWidth={1.5} />
                  </div>
                  <span className="font-body text-xs font-semibold text-[#C85A40] bg-[#C85A40]/10 px-3 py-1">{value.metric}</span>
                </div>
                <h3 className="font-display text-xl font-bold text-[#111C16] mb-3">{value.title}</h3>
                <p className="font-body text-sm text-[#4A5D53] leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-24 bg-white" data-testid="about-expertise">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="eyebrow text-[#C85A40] mb-4">Expert Guidance</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[#111C16] tracking-tighter">Attorneys Who Understand Business</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {[
              { role: 'Trademark Strategy', title: 'Brand Protection Experts', desc: 'Navigate complex trademark searches, overcome USPTO objections, and secure registrations in competitive markets. 500+ successful filings annually.', icon: Shield },
              { role: 'Business IP Strategy', title: 'Portfolio Architects', desc: 'Build comprehensive IP portfolios aligned with your growth strategy. We protect your current brand and your future expansion plans.', icon: Lightbulb },
              { role: 'Enforcement & Defense', title: 'Market Protection', desc: 'Proactive monitoring and enforcement to stop infringers. We do not just file — we defend your rights and eliminate marketplace threats.', icon: Scale }
            ].map((member, index) => (
              <div key={index} className="group p-8 bg-white border border-[#D5D1C8] hover-lift hover-gold-border">
                <div className="w-12 h-12 bg-[#1A3628] flex items-center justify-center mb-6 group-hover:bg-[#C85A40] transition-colors duration-300">
                  <member.icon className="h-6 w-6 text-white" strokeWidth={1.5} />
                </div>
                <p className="eyebrow text-[#C85A40] mb-2">{member.role}</p>
                <h3 className="font-display text-xl font-bold text-[#111C16] mb-3">{member.title}</h3>
                <p className="font-body text-sm text-[#4A5D53] leading-relaxed">{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      {/* <section className="relative py-16 md:py-20 bg-[#1A3628] overflow-hidden" data-testid="about-cta">
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at 50% 50%, #2D5A43 0%, transparent 70%)' }} />
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tighter mb-6">Ready to Protect Your Brand?</h2>
          <p className="font-body text-lg text-white/50 mb-12 max-w-xl mx-auto leading-relaxed">
            Let our expert team secure your intellectual property rights with precision and efficiency.
          </p>
          <Button onClick={() => handleButtonClick('Start Registration - About CTA')} className="bg-[#C85A40] hover:bg-[#A64730] text-white btn-hover" data-testid="about-cta-start-btn">
            Start Trademark Registration
          </Button>
        </div>
      </section> */}

      <InquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonContext={buttonContext} />
    </div>
  );
};

export default About;
